// Invoice printable HTML template
const BRAND = '#0085E7';
const BRAND_DARK = '#005fa3';

// 国际汇款统一收到公司主账户，不随销售所属账户变化（2026-10-14 运营提供）
export const INTERNATIONAL_BANK = {
  beneficiaryName: 'Worldwide Holidays',
  accountNo: '12 3209 0139859 00',
  bankName: 'ASB BANK LTD (Auckland Saving Bank)',
  bankAddress: 'ASB NORTH WHARF, 12 JELLICOE STREET AUCKLAND 1010',
  swift: 'ASBBNZ2A',
  beneficiaryAddress: '14C 155 Queen Street AUCKLAND 1010',
};

// Notes 是多行自由文本，直接插进 HTML 会吞掉换行并可能带入标签，统一转义后把 \n 换成 <br>
const nl2br = (s) => String(s || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/\r?\n/g, '<br>');

export function generateInvoiceHtml(invoice) {
  const {
    invoiceNo = '',
    invoiceDate,
    dueDate,
    billToName = '',
    billToEmail = '',
    billToPhone = '',
    billToAddress = '',
    salesRepName = '',
    salesRepEmail = '',
    salesRepPhone = '',
    fromCompany = 'Worldwide Holidays Limited',
    gstNo = '70-820-390',
    website = 'wanguo.co.nz',
    items = [],
    gstPercent = 0,
    amountPaid = 0,
    reference = '',
    particulars = '',
    notes = '',
    paymentMode = 'domestic',
    bankInfo = {},
  } = invoice || {};

  // 银行信息只能来自门店快照，绝不能有默认账号兜底（客户会付错款）
  const bank = {
    bankName: bankInfo?.bankName || '',
    bankAccountName: bankInfo?.bankAccountName || '',
    bankAccountNo: bankInfo?.bankAccountNo || '',
    bankSwift: bankInfo?.bankSwift || '',
  };
  const hasBank = !!bank.bankAccountNo;
  // 国际汇款账户取开票时存下的快照；2026-10-14 之前的发票没有快照，保持原样式（销售账户 + 旧银行地址）
  const intl = bankInfo?.international || null;

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-NZ', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const dateStr = fmt(invoiceDate) || fmt(new Date());
  const dueStr = fmt(dueDate);

  const safeItems = Array.isArray(items) ? items : [];
  const subtotal = safeItems.reduce((s, i) => s + (Number(i.amount) || 0) * (Number(i.quantity) || 1), 0);
  const gstableSubtotal = safeItems.reduce((s, i) => i.gst ? s + (Number(i.amount) || 0) * (Number(i.quantity) || 1) : s, 0);
  const gstPct = Number(gstPercent) || 0;
  const gstAmount = gstPct > 0 ? gstableSubtotal * gstPct / (100 + gstPct) : 0;
  const total = subtotal;
  const paid = Number(amountPaid) || 0;
  const amountDue = total - paid;

  // ?v= 是浏览器缓存的破缓存键，不是装饰：public/ 下的文件名不带内容哈希，
  // 而 nginx 给静态资源发的是 `max-age=31536000, immutable`——换图不改这个版本号，
  // 打开过发票页的人最长一年都还在用旧 logo（2026-08-28 换 logo 时踩到）。以后换图必须 +1。
  const LOGO_VERSION = 2;
  const logoPath = `/logo-wanguuo.png?v=${LOGO_VERSION}`;
  const logoUrl = typeof window !== 'undefined' ? `${window.location.origin}${logoPath}` : logoPath;

  const row = (label, value, bold) => value
    ? `<tr><td style="color:#888;padding-right:10px;white-space:nowrap">${label}</td><td${bold ? ' style="font-weight:600"' : ''}>${value}</td></tr>`
    : '';

  const paymentBlock = !hasBank ? '' : (paymentMode === 'international' ? `
<div style="font-size:12px;font-weight:bold;color:${BRAND_DARK};margin-bottom:5px">International Transfer</div>
<table cellpadding="2" cellspacing="0" border="0" style="font-size:12px;color:#444;line-height:1.7">
${intl ? `${row('Beneficiary', intl.beneficiaryName)}
${row('Account No.', intl.accountNo, true)}
${row('Bank', intl.bankName)}
${row('Bank Address', intl.bankAddress)}
${row('SWIFT', intl.swift, true)}
${row('Address', intl.beneficiaryAddress)}` : `${row('Bank', bank.bankName)}
${row('SWIFT', bank.bankSwift, true)}
${row('Account No.', bank.bankAccountNo, true)}
${row('Bank Addr.', '23-29 Albert St, Auckland 1010, NZ')}`}
${row('Reference', reference, true)}
${row('Particulars', particulars, true)}</table>
` : `
<div style="font-size:12px;font-weight:bold;color:${BRAND_DARK};margin-bottom:5px">Domestic Transfer (NZD)</div>
<table cellpadding="2" cellspacing="0" border="0" style="font-size:12px;color:#444;line-height:1.7">
${row('Bank', bank.bankName)}
${row('Account Name', bank.bankAccountName)}
${row('Account No.', bank.bankAccountNo, true)}
${row('Reference', reference, true)}
${row('Particulars', particulars, true)}</table>
`);

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><title>Invoice ${invoiceNo}</title>
<style>
html,body{margin:0;padding:0;background:#fff;font-family:Arial,Helvetica,sans-serif;color:#333;font-size:14px}
.page{display:flex;flex-direction:column;min-height:100vh;max-width:720px;margin:0 auto;box-sizing:border-box;padding:0 10px}
.content{flex:0 0 auto}
.spacer{flex:1 1 auto}
.footer{flex:0 0 auto}
@media print{body{background:#fff!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}.no-print{display:none!important}@page{margin:15mm 10mm;size:A4}.page{min-height:calc(297mm - 30mm)}}
</style>
</head><body>
<div class="page"><div class="content">

<!-- Header: logo + INVOICE + meta -->
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-bottom:3px solid ${BRAND};padding-bottom:14px;margin-bottom:18px">
<tr>
<td style="vertical-align:top;width:50%">
<img src="${logoUrl}" alt="${fromCompany}" style="display:block;width:230px;height:auto" onerror="this.style.display='none'"></td>
<td style="vertical-align:top;text-align:right;width:50%">
<div style="font-size:30px;font-weight:bold;color:${BRAND};letter-spacing:1px;margin-bottom:10px">INVOICE</div>
<table cellpadding="2" cellspacing="0" border="0" style="font-size:13px;margin-left:auto">
<tr><td style="color:#888;padding-right:14px;text-align:right">Invoice no.</td><td style="font-weight:bold;color:${BRAND_DARK};text-align:right">${invoiceNo}</td></tr>
<tr><td style="color:#888;padding-right:14px;text-align:right">Invoice date</td><td style="text-align:right">${dateStr}</td></tr>
${dueStr ? `<tr><td style="color:#888;padding-right:14px;text-align:right">Due Date</td><td style="text-align:right">${dueStr}</td></tr>` : ''}
</table></td>
</tr></table>

<!-- From / Bill To -->
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:18px">
<tr>
<td style="vertical-align:top;width:50%">
<div style="font-size:13px;font-weight:bold;color:#222;margin-bottom:6px">From</div>
<div style="font-size:15px;font-weight:bold;color:${BRAND_DARK};text-transform:uppercase;margin-bottom:4px;white-space:nowrap">${fromCompany}</div>
<div style="font-size:13px;color:#555;line-height:1.8">GST No: ${gstNo}<br>Website: ${website}</div>
</td>
<td style="vertical-align:top;width:50%;text-align:right">
<div style="font-size:13px;font-weight:bold;color:#222;margin-bottom:6px">Bill to</div>
<div style="font-size:15px;font-weight:bold;color:${BRAND_DARK};margin-bottom:4px">${billToName}</div>
${billToEmail ? `<div style="font-size:13px;color:#555">${billToEmail}</div>` : ''}
${billToPhone ? `<div style="font-size:13px;color:#555">${billToPhone}</div>` : ''}
${billToAddress ? `<div style="font-size:13px;color:#555">${billToAddress}</div>` : ''}
</td>
</tr></table>

${salesRepName ? `<div style="background:#f0f8ff;border-left:3px solid ${BRAND};padding:10px 16px;border-radius:0 4px 4px 0;margin-bottom:14px">
<span style="font-size:13px;color:#888">Sales Rep: </span><span style="font-size:14px;font-weight:600;color:${BRAND_DARK}">${salesRepName}</span>
${salesRepEmail ? `<span style="font-size:13px;color:#666"> &nbsp;${salesRepEmail}</span>` : ''}${salesRepPhone ? `<span style="font-size:13px;color:#666"> &nbsp;${salesRepPhone}</span>` : ''}</div>` : ''}

<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin-bottom:8px">
<tr style="background:${BRAND}">
<td style="padding:10px 12px;font-size:12px;font-weight:bold;color:#fff;text-transform:uppercase;letter-spacing:0.5px;border-radius:4px 0 0 0">Description</td>
<td style="padding:10px 12px;font-size:12px;font-weight:bold;color:#fff;text-transform:uppercase;letter-spacing:0.5px;text-align:center;width:50px">Qty</td>
<td style="padding:10px 12px;font-size:12px;font-weight:bold;color:#fff;text-transform:uppercase;letter-spacing:0.5px;text-align:right;width:110px;white-space:nowrap">Unit Price (NZD)</td>
<td style="padding:10px 12px;font-size:12px;font-weight:bold;color:#fff;text-transform:uppercase;letter-spacing:0.5px;text-align:center;width:50px">GST</td>
<td style="padding:10px 12px;font-size:12px;font-weight:bold;color:#fff;text-transform:uppercase;letter-spacing:0.5px;text-align:right;border-radius:0 4px 0 0;width:110px;white-space:nowrap">Amount (NZD)</td></tr>
${safeItems.map((item, i) => `<tr style="background:${i % 2 === 0 ? '#fff' : '#f8fbff'}">
<td style="padding:10px 12px;font-size:14px;color:#333;border-bottom:1px solid #e8ecf0">${item.description || ''}</td>
<td style="padding:10px 12px;font-size:14px;color:#333;border-bottom:1px solid #e8ecf0;text-align:center">${item.quantity || 1}</td>
<td style="padding:10px 12px;font-size:14px;color:#333;border-bottom:1px solid #e8ecf0;text-align:right">${Number(item.amount || 0).toFixed(2)}</td>
<td style="padding:10px 12px;font-size:14px;color:#333;border-bottom:1px solid #e8ecf0;text-align:center">${item.gst ? '✓' : ''}</td>
<td style="padding:10px 12px;font-size:14px;color:#333;border-bottom:1px solid #e8ecf0;text-align:right">${(Number(item.amount || 0) * (Number(item.quantity) || 1)).toFixed(2)}</td></tr>`).join('')}
</table>

</div><div class="spacer"></div><div class="bottom" style="border-top:1px solid #d6e8f8;padding-top:14px;margin-top:14px">

<table width="100%"><tr><td style="width:55%">&nbsp;</td><td style="width:45%"><table width="100%">
<tr><td style="padding:6px 0;font-size:13px;color:#666">Subtotal</td><td style="padding:6px 0;font-size:13px;text-align:right">${subtotal.toFixed(2)}</td></tr>
<tr><td style="padding:6px 0;font-size:13px;color:#666;border-bottom:1px solid #e8ecf0">Includes GST (${gstPct.toFixed(1)}%)</td><td style="padding:6px 0;font-size:13px;text-align:right;border-bottom:1px solid #e8ecf0">${gstAmount.toFixed(2)}</td></tr>
<tr style="background:${BRAND}"><td style="padding:8px 12px;font-size:14px;font-weight:bold;color:#fff;border-radius:4px 0 0 4px">Total (NZD)</td><td style="padding:8px 12px;font-size:14px;font-weight:bold;color:#fff;text-align:right;border-radius:0 4px 4px 0">${total.toFixed(2)}</td></tr>
<tr><td style="padding:6px 0;font-size:13px;color:#666">Amount Paid</td><td style="padding:6px 0;font-size:13px;color:#10b981;text-align:right">${paid.toFixed(2)}</td></tr>
<tr><td style="padding:8px 0;font-size:15px;font-weight:bold">Amount Due</td><td style="padding:8px 0;font-size:16px;font-weight:bold;color:${amountDue > 0 ? '#dc2626' : '#10b981'};text-align:right">NZD ${amountDue.toFixed(2)}</td></tr>
</table></td></tr></table>

${hasBank ? `<div style="background:#f7fbff;border:1px solid #d6e8f8;border-radius:5px;padding:14px 18px;margin-top:14px">
<div style="font-size:12px;font-weight:bold;color:${BRAND};text-transform:uppercase;letter-spacing:0.5px;margin-bottom:10px">Payment Details</div>
${paymentBlock}
</div>` : ''}

${notes ? `<div style="background:#fef9ec;border-left:3px solid #f59e0b;padding:10px 14px;border-radius:0 4px 4px 0;margin-top:12px">
<div style="font-size:12px;font-weight:bold;color:#92400e;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px">Notes</div><div style="font-size:13px;color:#78350f;line-height:1.6">${nl2br(notes)}</div></div>` : ''}

</div><div class="footer" style="padding:14px 0 0;border-top:2px solid ${BRAND};text-align:center;margin-top:14px">
<div style="font-size:15px;font-weight:bold;color:${BRAND};margin-bottom:5px">Thank you for your business!</div>
<div style="font-size:12px;color:#888;line-height:1.5">${fromCompany} | GST: ${gstNo} | ${website}</div>
</div></div></body></html>`;
}

export function openInvoicePrintWindow(invoice) {
  const html = generateInvoiceHtml(invoice);
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  w.document.title = ' ';
  w.onload = () => { w.document.title = ' '; w.print(); };
  return true;
}
